import { useLazyQuery } from '@apollo/client';
import { useContext, useEffect, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { Context } from '../../../../../context/AppContext';
import { MAX_SUGGESTIONS, SEARCH_AREA_INPUT_ID } from '../../../constants';
import { useSearchContext } from '../../../context/Search';
import { getNestedValue, isNotEmptyArray } from '../../utils/helpers';
import SearchAreaSearchQuery from './SearchAreaSearchQuery';

/**
 * Transforms fetched data object into an array of search area suggestions
 * @param {Object} data The fetched data
 * @returns {Array} Array of search area suggestions
 */
const transformData = data => {
  const edges = getNestedValue(data, 'searchAreaSearch', 'edges');
  if (!isNotEmptyArray(edges)) return [];
  return edges.map(
    ({ node: { description, id, name, path, statisticalAreas } }) => ({
      id,
      name,
      path,
      statisticalAreaIds: statisticalAreas.map(sA => sA.id),
      suggestLabel: description,
    })
  );
};

/**
 * Logic for fetching and returning the user's current location
 * @returns {Object} {
 * browserSupportsGeolocation,
 * error,
 * fetchMyLocation,
 * loading,
 * }
 */
const useMyLocation = () => {
  const {
    searchAreaSuggest: { setSearchAreaSuggestions },
  } = useSearchContext();
  const { setFocus, setValue } = useFormContext();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState();
  const [coords, setCoords] = useState();
  const browserSupportsGeolocation = 'geolocation' in navigator;
  const [
    fetchSearchAreaSuggestions,
    { data, error: fetchError, loading: fetchLoading },
  ] = useLazyQuery(SearchAreaSearchQuery);
  const {
    media: { mobile },
  } = useContext(Context);

  const maxSuggestions = mobile
    ? MAX_SUGGESTIONS.MOBILE.SEARCH_AREAS
    : MAX_SUGGESTIONS.DESKTOP.SEARCH_AREAS;

  const saveSuggestions = () => {
    setLoading(false);
    setFocus(SEARCH_AREA_INPUT_ID);
    const suggestions = transformData(data);
    setSearchAreaSuggestions(suggestions);
    setValue(SEARCH_AREA_INPUT_ID, suggestions[0].name);
  };

  /**
   * Run this function when the browser is successfully able to obtain the user's location
   * @param {Object} position.coords.latitude The user's latitude as provided by the browser
   * @param {Object} position.coords.longitude The user's longitude as provided by the browser
   * @returns {void}
   */
  const onSuccess = ({ coords: { latitude, longitude } }) => {
    // If coordinates have already been saved to state and haven't changed, then the suggestions
    // wouldn't have changed either and another fetch from the server won't be required. Simply re-save
    // the fetched search area suggestions.
    if (
      coords &&
      coords.latitude === latitude &&
      coords.longitude === longitude
    ) {
      saveSuggestions();
    } else if (latitude && longitude) {
      // If coordinates have changed or are not saved to state, then fetch the suggestions.
      setCoords({ latitude, longitude });
      fetchSearchAreaSuggestions({
        variables: {
          coordinates: `POINT (${longitude} ${latitude})`,
          first: maxSuggestions,
          enabled: true,
        },
      });
    }
  };

  const onError = err => {
    setError(err);
    setLoading(false);
  };

  /**
   * Fetch the user's location using the browser API
   * NOTE: The user will be prompted by the browser to grant location access permissions, if not already authorised
   */
  const fetchMyLocation = () => {
    setLoading(true);
    navigator.geolocation.getCurrentPosition(onSuccess, onError, {
      enableHighAccuracy: true,
      timeout: 10000,
    });
  };

  useEffect(() => {
    setLoading(fetchLoading);
  }, [fetchLoading]);

  useEffect(() => {
    if (fetchError) {
      onError(fetchError);
    }
  }, [fetchError]);

  // When search areas are found, save them as suggestions
  useEffect(() => {
    const hasSuggestions = isNotEmptyArray(
      getNestedValue(data, 'searchAreaSearch', 'edges')
    );
    if (hasSuggestions) {
      saveSuggestions();
    }
  }, [data]);

  return {
    browserSupportsGeolocation,
    error,
    fetchMyLocation,
    loading,
  };
};

export default useMyLocation;
